import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { IProductDetail } from '@courses/types-layer/types/product'

/**
 * Product UI Store - handles only UI state
 * NO server data, NO service imports, NO API calls
 */
export const useProductUIStore = defineStore('product-ui', () => {
  const selectedTemplate = ref<string>('')
  const showCustomizationModal = ref(false)
  const customizationMode = ref<'edit' | 'preview'>('edit')
  const isLoadingCustomization = ref(false)

  // ===== CURRENT PRODUCT STATE =====
  const currentProduct = ref<IProductDetail | null>(null)
  const isCurrentProductLoading = ref(false)
  const isInitialized = ref(false)

  // ===== COMPUTED GETTERS =====
  const productId = ref('')
  const productLocationId = ref('')

  const setSelectedTemplate = (template: string) => {
    selectedTemplate.value = template
  }

  const setShowCustomizationModal = (show: boolean) => {
    showCustomizationModal.value = show
  }

  const setCustomizationMode = (mode: 'edit' | 'preview') => {
    customizationMode.value = mode
  }

  const setIsLoadingCustomization = (loading: boolean) => {
    isLoadingCustomization.value = loading
  }

  const toggleCustomizationModal = () => {
    showCustomizationModal.value = !showCustomizationModal.value
  }

  // ===== CURRENT PRODUCT ACTIONS =====
  const setCurrentProduct = (product: IProductDetail | null) => {
    currentProduct.value = product
  }

  const setIsCurrentProductLoading = (loading: boolean) => {
    isCurrentProductLoading.value = loading
  }

  const setInitialized = (value: boolean) => {
    isInitialized.value = value
  }

  const setProductId = (id: any) => {
    productId.value = id
  }

  const setProductLocationId = (locationId: string) => {
    productLocationId.value = locationId
  }

  const reset = () => {
    selectedTemplate.value = ''
    showCustomizationModal.value = false
    customizationMode.value = 'edit'
    isLoadingCustomization.value = false
    currentProduct.value = null
    isCurrentProductLoading.value = false
    isInitialized.value = false
  }

  return {
    selectedTemplate: computed(() => selectedTemplate.value),
    showCustomizationModal: computed(() => showCustomizationModal.value),
    customizationMode: computed(() => customizationMode.value),
    isLoadingCustomization: computed(() => isLoadingCustomization.value),

    // Current product state
    currentProduct: computed(() => currentProduct.value),
    isCurrentProductLoading: computed(() => isCurrentProductLoading.value),
    isInitialized: computed(() => isInitialized.value),
    productId,
    productLocationId,

    setProductId,
    setProductLocationId,

    setSelectedTemplate,
    setShowCustomizationModal,
    setCustomizationMode,
    setIsLoadingCustomization,
    toggleCustomizationModal,

    // Current product actions
    setCurrentProduct,
    setIsCurrentProductLoading,
    setInitialized,

    reset,
  }
})
